import { useRef, useEffect, useState } from 'react';
import YouTube, { type YouTubeEvent, type YouTubePlayer } from 'react-youtube';
import getVideoId from 'get-video-id';

import OPE from 'js/on-page-editing';
import useOnIntersect from 'js/hooks/use-on-intersect';

import { IYoutubeVideo } from 'source/NFR.types';

interface YouTubePlayerProps extends IYoutubeVideo {
  play?: boolean;
}

const YoutubeVideo: React.FC<YouTubePlayerProps> = ({ onPageEditing, urlOrEmbed = '', play }) => {
  const playerRef = useRef<YouTubePlayer | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [intersected, setIntersected] = useState(false);
  const { id, service } = getVideoId(urlOrEmbed);

  useOnIntersect(wrapperRef, () => setIntersected(true));

  useEffect(() => {
    const player = playerRef.current;
    if (player && play) {
      void player.playVideo();
      const iframe = (player as unknown as { f: HTMLIFrameElement }).f;
      if (iframe.requestFullscreen) {
        void iframe.requestFullscreen();
      } else {
        const iframeWithPrefixes = iframe as HTMLIFrameElement & {
          webkitRequestFullScreen?: () => void;
          mozRequestFullScreen?: () => void;
          msRequestFullscreen?: () => void;
        };
        if (iframeWithPrefixes.webkitRequestFullScreen) {
          iframeWithPrefixes.webkitRequestFullScreen();
        } else if (iframeWithPrefixes.mozRequestFullScreen) {
          iframeWithPrefixes.mozRequestFullScreen();
        } else if (iframeWithPrefixes.msRequestFullscreen) {
          iframeWithPrefixes.msRequestFullscreen();
        }
      }
    }
  }, [play]);

  // According to the spec, only youtube should be supported:
  // https://confluence.ad.forskningsradet.no/display/NYWEB/Embed-blokker
  if (service !== 'youtube' || !id) {
    return <div className="youtube-video" {...OPE(onPageEditing)} ref={wrapperRef} />;
  }

  const opts = {
    width: '100%',
    playerVars: {
      autoplay: 0 as const,
      modestbranding: 1 as const,
      rel: 0 as const
    }
  };

  const shouldLoad = intersected || play;

  return (
    <div className="youtube-video" {...OPE(onPageEditing)} ref={wrapperRef}>
      {shouldLoad && (
        <YouTube
          className="youtube-video--iframe-container"
          opts={opts}
          videoId={id}
          onReady={(e: YouTubeEvent) => (playerRef.current = e.target)}
          title="Youtube video"
        />
      )}
    </div>
  );
};

YoutubeVideo.displayName = 'YoutubeVideo';

export default YoutubeVideo;
